/**
 * wa-multi-select — form-associated multi-select web component
 *
 * Displays selected values as removable tag chips in the trigger, with an
 * inline search filter, "Select all" toggle with tri-state checkbox, clear-all
 * button, and full keyboard navigation.
 *
 * Usage:
 *   <script type="module" src="wa-multi-select.js"></script>
 *   <wa-multi-select name="tags" placeholder="Choose tags…">
 *     <option value="a" selected>Apple</option>
 *     <option value="b">Banana</option>
 *     <optgroup label="Citrus">
 *       <option value="l">Lemon</option>
 *       <option value="o">Orange</option>
 *     </optgroup>
 *   </wa-multi-select>
 *
 * Attributes:  name, placeholder, disabled, required
 * Properties:  .values (array, read/write), .value (first selected, read/write),
 *              .disabled, .required, .name, .placeholder
 * Events:      change (bubbles, composed)
 *
 * Keyboard:
 *   ArrowDown/Up — navigate options          Enter/Space — toggle highlighted
 *   Escape       — close & clear search      Backspace   — remove last tag
 *   Tab          — close and move focus
 *
 * Theming — CSS custom properties on the host element:
 *   --wa-font              --wa-font-size         --wa-min-width
 *   --wa-bg                --wa-color             --wa-border
 *   --wa-border-hover      --wa-border-focus      --wa-radius
 *   --wa-placeholder-color --wa-arrow-color
 *   --wa-tag-bg            --wa-tag-color         --wa-tag-border
 *   --wa-cleaner-color     --wa-cleaner-hover-color
 *   --wa-menu-bg           --wa-menu-border       --wa-menu-color
 *   --wa-option-hover
 *   --wa-option-selected-bg  --wa-option-selected-color
 *   --wa-option-selected-multi-bg
 *   --wa-checkbox-border
 *   --wa-group-color       --wa-group-divider
 *
 * Parts (::part() styling):
 *   trigger, tag, tag-remove, tag-remove-icon, chevron, search, dropdown, option
 */

class WaMultiSelect extends HTMLElement {
  static formAssociated = true;

  /** @type {ElementInternals} */ #internals;
  /** @type {ShadowRoot} */       #shadow;
  /** @type {HTMLDivElement} */   #triggerEl;
  /** @type {HTMLInputElement} */ #searchInput;
  /** @type {HTMLDivElement} */   #selectionEl;
  /** @type {HTMLButtonElement} */#cleanerBtn;
  /** @type {HTMLButtonElement} */#selectAllBtn;
  /** @type {HTMLUListElement} */ #optionsList;

  /** @type {Array<{value:string,text:string,disabled:boolean}>} */
  #items = [];

  #values        = new Set();
  #defaultValues = [];
  #highlighted   = -1;
  #mo            = null;

  #uid = `wams${Math.random().toString(36).slice(2, 8)}`;

  // ── Lifecycle ────────────────────────────────────────────────────────────────

  constructor() {
    super();
    this.#internals = this.attachInternals();
    this.#shadow    = this.attachShadow({ mode: 'open' });
    this.#shadow.innerHTML = this.#buildTemplate();

    this.#triggerEl    = this.#shadow.querySelector('.trigger');
    this.#searchInput  = this.#shadow.querySelector('.search-input');
    this.#selectionEl  = this.#shadow.querySelector('.selection');
    this.#cleanerBtn   = this.#shadow.querySelector('.cleaner');
    this.#selectAllBtn = this.#shadow.querySelector('.select-all-btn');
    this.#optionsList  = this.#shadow.querySelector('.options-list');

    this.#triggerEl.addEventListener('mousedown', (e) => this.#onTriggerMouseDown(e));
    this.#searchInput.addEventListener('input',   ()  => this.#onSearchInput());
    this.#searchInput.addEventListener('keydown', (e) => this.#onSearchKeyDown(e));
    this.#cleanerBtn.addEventListener('click',    (e) => { e.stopPropagation(); this.#clearAll(); });
    this.#selectAllBtn.addEventListener('mousedown', (e) => {
      e.preventDefault();
      e.stopPropagation();
      this.#toggleSelectAll();
    });
  }

  static get observedAttributes() {
    return ['disabled', 'placeholder', 'required'];
  }

  attributeChangedCallback(name) {
    if (!this.#triggerEl) return;
    if (name === 'disabled' && this.disabled) this.#closeDropdown();
    if (name === 'placeholder') this.#syncPlaceholder();
  }

  connectedCallback() {
    this.#buildList();
    this.#defaultValues = [...this.#values];
    this.#syncFormValue();
    this.#renderTags();
    requestAnimationFrame(() => this.#syncTriggerLabel());

    this.#mo = new MutationObserver(() => {
      this.#buildList();
      this.#renderTags();
    });
    this.#mo.observe(this, { childList: true, subtree: true, characterData: true });
  }

  disconnectedCallback() {
    this.#mo?.disconnect();
    this.#closeDropdown();
  }

  formResetCallback() {
    this.#applyValues(new Set(this.#defaultValues), false);
  }

  // ── Public API ──────────────────────────────────────────────────────────────

  get values()    { return [...this.#values]; }
  set values(v)   { this.#applyValues(new Set(v.map(String)), true); }

  get value()     { return [...this.#values][0] ?? ''; }
  set value(v)    { this.#applyValues(v ? new Set([String(v)]) : new Set(), true); }

  get disabled()  { return this.hasAttribute('disabled'); }
  set disabled(v) { this.toggleAttribute('disabled', Boolean(v)); }

  get required()  { return this.hasAttribute('required'); }
  set required(v) { this.toggleAttribute('required', Boolean(v)); }

  get name()        { return this.getAttribute('name') ?? ''; }
  get placeholder() { return this.getAttribute('placeholder') ?? 'Select…'; }

  // ── List building ────────────────────────────────────────────────────────────

  #buildList() {
    this.#items = [];
    this.#optionsList.innerHTML = '';

    // Seed from option[selected]; also preserve any user-made selections that still exist
    const domSelected   = new Set([...this.querySelectorAll('option[selected]')].map(o => o.value));
    const existingValues = new Set([...this.querySelectorAll('option')].map(o => o.value));
    this.#values = new Set([...this.#values, ...domSelected].filter(v => existingValues.has(v)));

    for (const child of this.children) {
      if (child.tagName === 'OPTGROUP') {
        this.#appendGroup(child);
      } else if (child.tagName === 'OPTION') {
        this.#appendOptionEl(child, false);
      }
    }

    this.#syncSelectedDOM();
    this.#filterOptions(this.#searchInput.value);
  }

  #appendGroup(group) {
    const li = document.createElement('li');
    li.className = 'group-label';
    li.setAttribute('role', 'presentation');
    li.textContent = group.getAttribute('label') ?? '';
    this.#optionsList.appendChild(li);

    for (const opt of group.querySelectorAll(':scope > option')) {
      this.#appendOptionEl(opt, group.disabled);
    }
  }

  #appendOptionEl(opt, groupDisabled) {
    const i = this.#items.length;
    const item = {
      value:    opt.value,
      text:     opt.textContent.trim(),
      disabled: opt.disabled || groupDisabled,
    };
    this.#items.push(item);

    const li = document.createElement('li');
    li.className = 'option';
    li.setAttribute('role', 'option');
    li.setAttribute('part', 'option');
    li.setAttribute('id', `${this.#uid}-opt-${i}`);
    li.setAttribute('aria-selected', 'false');
    if (item.disabled) li.setAttribute('aria-disabled', 'true');

    const check = document.createElement('span');
    check.className = 'option-check';
    check.setAttribute('aria-hidden', 'true');
    check.innerHTML = `<svg width="11" height="11" viewBox="0 0 12 12" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M2 6L5 9L10 3"/></svg>`;

    const text = document.createElement('span');
    text.className = 'option-text';
    text.textContent = item.text;

    li.appendChild(check);
    li.appendChild(text);

    li.addEventListener('mousedown', (e) => {
      e.preventDefault();
      if (!item.disabled) this.#toggle(i);
    });
    li.addEventListener('mousemove', () => {
      if (!item.disabled && !li.hidden) this.#setHighlight(i);
    });

    this.#optionsList.appendChild(li);
  }

  // ── Tag rendering ─────────────────────────────────────────────────────────────

  #renderTags() {
    this.#selectionEl.querySelectorAll('.tag').forEach(t => t.remove());

    for (const v of this.#values) {
      const item = this.#items.find(it => it.value === v);
      if (!item) continue;

      const tag = document.createElement('span');
      tag.className = 'tag';
      tag.setAttribute('part', 'tag');

      const label = document.createElement('span');
      label.className = 'tag-text';
      label.textContent = item.text;

      const removeBtn = document.createElement('button');
      removeBtn.type = 'button';
      removeBtn.className = 'tag-remove';
      removeBtn.setAttribute('part', 'tag-remove');
      removeBtn.setAttribute('aria-label', `Remove ${item.text}`);
      removeBtn.innerHTML = `<svg part="tag-remove-icon" width="8" height="8" viewBox="0 0 10 10" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" aria-hidden="true"><line x1="1" y1="1" x2="9" y2="9"/><line x1="9" y1="1" x2="1" y2="9"/></svg>`;

      removeBtn.addEventListener('mousedown', (e) => { e.preventDefault(); e.stopPropagation(); });
      removeBtn.addEventListener('click', () => {
        const next = new Set(this.#values);
        next.delete(v);
        this.#applyValues(next, true);
        this.#searchInput.focus();
      });

      tag.appendChild(label);
      tag.appendChild(removeBtn);
      this.#selectionEl.insertBefore(tag, this.#searchInput);
    }

    this.#syncPlaceholder();
    this.#syncCleaner();
  }

  // ── State sync ─────────────────────────────────────────────────────────────────

  #syncSelectedDOM() {
    let i = 0;
    for (const li of this.#optionsList.children) {
      if (li.classList.contains('option')) {
        li.setAttribute('aria-selected', this.#values.has(this.#items[i]?.value) ? 'true' : 'false');
        i++;
      }
    }
    this.#syncSelectAll();
  }

  #syncSelectAll() {
    const eligible = this.#items.filter(it => !it.disabled);
    const total    = eligible.length;
    const sel      = eligible.filter(it => this.#values.has(it.value)).length;
    const state    = total === 0 || sel === 0 ? 'none'
                   : sel === total             ? 'all'
                   :                            'indeterminate';
    this.#selectAllBtn.dataset.state = state;
    const labelEl = this.#selectAllBtn.querySelector('.select-all-label');
    if (labelEl) labelEl.textContent = state === 'all' ? 'Deselect all' : 'Select all';
  }

  #syncFormValue() {
    if (this.#values.size) {
      const fd = new FormData();
      for (const v of this.#values) fd.append(this.name, v);
      this.#internals.setFormValue(fd);
    } else {
      this.#internals.setFormValue(null);
    }
    this.#syncValidity();
  }

  #syncValidity() {
    if (this.required && this.#values.size === 0) {
      this.#internals.setValidity(
        { valueMissing: true },
        'Please select at least one option.',
        this.#searchInput
      );
    } else {
      this.#internals.setValidity({});
    }
  }

  #syncCleaner() {
    this.#cleanerBtn.hidden = this.#values.size === 0;
  }

  #syncPlaceholder() {
    this.#searchInput.placeholder = this.#values.size === 0 ? this.placeholder : '';
  }

  #syncTriggerLabel() {
    const labels = this.#internals.labels;
    if (labels.length) {
      const text = [...labels].map(l => l.textContent.trim()).filter(Boolean).join(' ');
      this.#searchInput.setAttribute('aria-label', text);
    }
  }

  // ── Selection ─────────────────────────────────────────────────────────────────

  #toggle(idx) {
    const item = this.#items[idx];
    if (!item || item.disabled) return;
    const next = new Set(this.#values);
    if (next.has(item.value)) { next.delete(item.value); } else { next.add(item.value); }
    this.#applyValues(next, true);
  }

  #toggleSelectAll() {
    const state    = this.#selectAllBtn.dataset.state;
    const eligible = this.#items.filter(it => !it.disabled).map(it => it.value);
    this.#applyValues(state === 'all' ? new Set() : new Set(eligible), true);
  }

  #clearAll() {
    this.#applyValues(new Set(), true);
    this.#searchInput.value = '';
    this.#filterOptions('');
    this.#searchInput.focus();
    if (!this.hasAttribute('open')) this.#openDropdown();
  }

  #applyValues(valSet, dispatch) {
    this.#values = valSet;
    this.#syncSelectedDOM();
    this.#syncFormValue();
    this.#renderTags();
    if (dispatch) {
      this.dispatchEvent(new Event('change', { bubbles: true, composed: true }));
    }
  }

  // ── Dropdown ─────────────────────────────────────────────────────────────────

  #openDropdown() {
    if (this.disabled) return;
    this.setAttribute('open', '');
    this.#searchInput.setAttribute('aria-expanded', 'true');
    const [firstVal] = this.#values;
    const idx = firstVal != null
      ? this.#items.findIndex(it => it.value === firstVal && !it.disabled)
      : -1;
    this.#setHighlight(idx >= 0 ? idx : this.#firstVisibleIdx());
    document.addEventListener('mousedown', this.#onDocMouseDown);
    document.addEventListener('keydown',   this.#onDocKey);
  }

  #closeDropdown() {
    this.removeAttribute('open');
    this.#searchInput?.setAttribute('aria-expanded', 'false');
    this.#setHighlight(-1);
    document.removeEventListener('mousedown', this.#onDocMouseDown);
    document.removeEventListener('keydown',   this.#onDocKey);
  }

  #firstVisibleIdx() {
    const visible = this.#visibleOptions();
    return visible.length ? this.#getItemIndex(visible[0]) : -1;
  }

  // ── Highlight ─────────────────────────────────────────────────────────────────

  #setHighlight(idx) {
    this.#optionsList.querySelectorAll('.option').forEach((li, i) => {
      li.classList.toggle('highlighted', i === idx);
    });
    this.#highlighted = idx;
    const allOpts = this.#optionsList.querySelectorAll('.option');
    if (idx >= 0 && allOpts[idx]) {
      allOpts[idx].scrollIntoView({ block: 'nearest' });
      this.#searchInput.setAttribute('aria-activedescendant', `${this.#uid}-opt-${idx}`);
    } else {
      this.#searchInput.removeAttribute('aria-activedescendant');
    }
  }

  #visibleOptions() {
    return [...this.#optionsList.querySelectorAll('.option:not([hidden])')];
  }

  #getItemIndex(li) {
    if (!li) return -1;
    return [...this.#optionsList.querySelectorAll('.option')].indexOf(li);
  }

  // ── Search / filter ──────────────────────────────────────────────────────────

  #filterOptions(query) {
    const q = query.trim().toLowerCase();
    let visibleCount = 0;
    let currentGroupLi = null;
    const groupCounts = new Map();
    let optIdx = 0;

    for (const li of this.#optionsList.children) {
      if (li.classList.contains('group-label')) {
        currentGroupLi = li;
        groupCounts.set(li, 0);
      } else if (li.classList.contains('option')) {
        const item = this.#items[optIdx++];
        const matches = !q || (item?.text ?? '').toLowerCase().includes(q);
        li.hidden = !matches;
        if (matches) {
          visibleCount++;
          if (currentGroupLi) groupCounts.set(currentGroupLi, (groupCounts.get(currentGroupLi) ?? 0) + 1);
        }
      }
    }

    for (const [groupLi, count] of groupCounts) groupLi.hidden = count === 0;

    let noResultsEl = this.#optionsList.querySelector('.no-results');
    if (visibleCount === 0) {
      if (!noResultsEl) {
        noResultsEl = document.createElement('li');
        noResultsEl.className = 'no-results';
        noResultsEl.setAttribute('role', 'presentation');
        noResultsEl.textContent = 'No results found';
        this.#optionsList.appendChild(noResultsEl);
      }
      noResultsEl.hidden = false;
    } else if (noResultsEl) {
      noResultsEl.hidden = true;
    }

    this.#setHighlight(-1);
  }

  // ── Event handlers ─────────────────────────────────────────────────────────────

  #onTriggerMouseDown = (e) => {
    if (this.disabled) return;
    if (e.target.closest('.tag-remove, .cleaner')) return;
    e.preventDefault();
    this.#searchInput.focus();
    if (e.target.closest('.tag')) return;
    if (e.target === this.#searchInput) {
      if (!this.hasAttribute('open')) this.#openDropdown();
      return;
    }
    this.hasAttribute('open') ? this.#closeDropdown() : this.#openDropdown();
  };

  #onSearchInput = () => {
    this.#filterOptions(this.#searchInput.value);
    if (!this.hasAttribute('open')) this.#openDropdown();
  };

  #onSearchKeyDown = (e) => {
    const isOpen = this.hasAttribute('open');
    switch (e.key) {
      case 'ArrowDown': {
        e.preventDefault();
        if (!isOpen) { this.#openDropdown(); return; }
        const vis = this.#visibleOptions();
        if (!vis.length) return;
        const cur = vis.findIndex(li => li.classList.contains('highlighted'));
        this.#setHighlight(this.#getItemIndex(vis[cur < vis.length - 1 ? cur + 1 : 0]));
        break;
      }
      case 'ArrowUp': {
        e.preventDefault();
        if (!isOpen) return;
        const vis = this.#visibleOptions();
        if (!vis.length) return;
        const cur = vis.findIndex(li => li.classList.contains('highlighted'));
        this.#setHighlight(this.#getItemIndex(vis[cur > 0 ? cur - 1 : vis.length - 1]));
        break;
      }
      case 'Enter':
        if (isOpen && this.#highlighted >= 0) {
          e.preventDefault();
          this.#toggle(this.#highlighted);
        } else if (!isOpen) {
          e.preventDefault();
          this.#openDropdown();
        }
        break;
      case ' ':
        if (isOpen && this.#highlighted >= 0) {
          e.preventDefault();
          this.#toggle(this.#highlighted);
        }
        break;
      case 'Escape':
        if (isOpen) {
          e.preventDefault();
          this.#closeDropdown();
          this.#searchInput.value = '';
          this.#filterOptions('');
        }
        break;
      case 'Backspace':
        if (!this.#searchInput.value) {
          const vals = [...this.#values];
          if (vals.length) {
            e.preventDefault();
            const next = new Set(this.#values);
            next.delete(vals[vals.length - 1]);
            this.#applyValues(next, true);
          }
        }
        break;
    }
  };

  #onDocMouseDown = (e) => {
    if (!e.composedPath().includes(this)) this.#closeDropdown();
  };

  #onDocKey = (e) => {
    if (e.key === 'Tab') this.#closeDropdown();
  };

  // ── Template ─────────────────────────────────────────────────────────────────

  #buildTemplate() {
    const listboxId = `${this.#uid}-listbox`;
    return `
<style>
  :host {
    display: block;
    position: relative;
    font-family: var(--wa-font, system-ui, -apple-system, sans-serif);
    font-size: var(--wa-font-size, 0.9375rem);
    line-height: 1.5;
  }

  :host([disabled]) { opacity: 0.5; pointer-events: none; }

  /* ── Trigger ── */
  .trigger {
    display: flex;
    align-items: center;
    gap: 6px;
    min-height: 44px;
    padding: 5px 8px 5px 10px;
    min-width: var(--wa-min-width, 220px);
    background: var(--wa-bg, #fff);
    border: 1.5px solid var(--wa-border, oklch(80% 0.02 260));
    border-radius: var(--wa-radius, 10px);
    cursor: text;
    box-shadow: 0 1px 3px oklch(0% 0 0 / 8%);
    transition: border-color 160ms ease, box-shadow 160ms ease;
  }

  .trigger:hover {
    border-color: var(--wa-border-hover, oklch(55% 0.22 270));
  }

  :host([open]) .trigger {
    border-color: var(--wa-border-focus, oklch(55% 0.22 270));
    box-shadow:
      0 0 0 2px var(--wa-focus-inner, #fff),
      0 0 0 4px var(--wa-border-focus, oklch(55% 0.22 270));
  }

  /* ── Selection area (tags + search input) ── */
  .selection {
    flex: 1;
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 4px;
    min-width: 0;
  }

  /* ── Tag chips ── */
  .tag {
    display: inline-flex;
    align-items: center;
    gap: 3px;
    padding: 2px 4px 2px 8px;
    background: var(--wa-tag-bg, oklch(92% 0.05 270));
    color: var(--wa-tag-color, oklch(28% 0.12 270));
    border: 1px solid var(--wa-tag-border, oklch(78% 0.1 270));
    border-radius: 6px;
    font-size: 0.85em;
    line-height: 1.4;
    white-space: nowrap;
    max-width: 200px;
  }

  .tag-text {
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .tag-remove {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    width: 18px;
    height: 18px;
    padding: 0;
    border: none;
    background: transparent;
    color: inherit;
    cursor: pointer;
    opacity: 0.5;
    border-radius: 3px;
    transition: opacity 100ms ease, background 100ms ease;
  }

  .tag-remove:hover {
    opacity: 1;
    background: oklch(55% 0.22 270 / 18%);
  }

  /* ── Search input ── */
  .search-input {
    flex: 1;
    min-width: 60px;
    border: none;
    outline: none;
    background: transparent;
    font: inherit;
    color: var(--wa-color, inherit);
    padding: 3px 0;
  }

  .search-input::placeholder {
    color: var(--wa-placeholder-color, oklch(60% 0.02 260));
  }

  /* ── Controls (cleaner + separator + chevron) ── */
  .controls {
    display: flex;
    align-items: center;
    gap: 4px;
    flex-shrink: 0;
    align-self: center;
  }

  .cleaner {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 22px;
    height: 22px;
    padding: 0;
    border: none;
    background: transparent;
    color: var(--wa-cleaner-color, oklch(52% 0.02 260));
    cursor: pointer;
    border-radius: 4px;
    transition: color 100ms ease, background 100ms ease;
  }

  .cleaner:hover {
    color: var(--wa-cleaner-hover-color, oklch(20% 0.02 260));
    background: oklch(0% 0 0 / 6%);
  }

  .separator {
    width: 1px;
    height: 16px;
    background: var(--wa-border, oklch(80% 0.02 260));
    flex-shrink: 0;
  }

  .chevron {
    flex-shrink: 0;
    width: 12px;
    height: 12px;
    color: var(--wa-arrow-color, oklch(55% 0.22 270));
    transition: rotate 160ms ease;
  }

  :host([open]) .chevron { rotate: 180deg; }

  /* ── Dropdown panel ── */
  .dropdown {
    position: absolute;
    inset-inline-start: 0;
    top: calc(100% + 4px);
    min-width: 100%;
    background: var(--wa-menu-bg, #fff);
    border: 1.5px solid var(--wa-menu-border, oklch(80% 0.02 260));
    border-radius: var(--wa-radius, 10px);
    box-shadow: 0 8px 24px oklch(0% 0 0 / 12%), 0 2px 6px oklch(0% 0 0 / 8%);
    z-index: 1000;
    overflow: hidden;
    visibility: hidden;
    opacity: 0;
    translate: 0 -4px;
    transition: opacity 160ms ease, translate 160ms ease, visibility 0s 160ms;
  }

  :host([open]) .dropdown {
    visibility: visible;
    opacity: 1;
    translate: 0 0;
    transition: opacity 160ms ease, translate 160ms ease;
  }

  /* ── Select-all header ── */
  .dropdown-header {
    padding: 5px;
    border-bottom: 1px solid var(--wa-menu-border, oklch(88% 0.02 260));
  }

  .select-all-btn {
    display: flex;
    align-items: center;
    gap: 8px;
    width: 100%;
    padding: 7px 10px;
    background: transparent;
    border: none;
    border-radius: calc(var(--wa-radius, 10px) - 5px);
    cursor: pointer;
    font: inherit;
    font-size: inherit;
    color: var(--wa-menu-color, inherit);
    text-align: left;
    transition: background 80ms ease;
  }

  .select-all-btn:hover {
    background: var(--wa-option-hover, oklch(90% 0.06 270));
  }

  /* Tri-state checkbox indicator */
  .select-all-indicator {
    flex-shrink: 0;
    width: 16px;
    height: 16px;
    border: 1.5px solid var(--wa-checkbox-border, oklch(65% 0.03 260));
    border-radius: 4px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: transparent;
    position: relative;
    transition: background 80ms ease, border-color 80ms ease, color 80ms ease;
  }

  .select-all-btn[data-state="all"] .select-all-indicator,
  .select-all-btn[data-state="indeterminate"] .select-all-indicator {
    background: var(--wa-option-selected-bg, oklch(55% 0.22 270));
    border-color: var(--wa-option-selected-bg, oklch(55% 0.22 270));
    color: var(--wa-option-selected-color, #fff);
  }

  .check-svg { display: none; }
  .dash-svg  { display: none; }
  .select-all-btn[data-state="all"]          .check-svg { display: block; }
  .select-all-btn[data-state="indeterminate"] .dash-svg { display: block; }

  /* ── Options list ── */
  .options-list {
    max-height: 240px;
    overflow-y: auto;
    padding: 5px;
    margin: 0;
    list-style: none;
  }

  .option {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 7px 10px;
    border-radius: calc(var(--wa-radius, 10px) - 4px);
    cursor: pointer;
    color: var(--wa-menu-color, inherit);
    white-space: nowrap;
    transition: background 80ms ease;
  }

  .option.highlighted,
  .option:hover {
    background: var(--wa-option-hover, oklch(90% 0.06 270));
  }

  .option[aria-selected="true"] {
    background: var(--wa-option-selected-multi-bg, oklch(55% 0.22 270 / 10%));
    font-weight: 500;
  }

  /* Hover always overrides selected tint */
  .option.highlighted[aria-selected="true"],
  .option:hover[aria-selected="true"] {
    background: var(--wa-option-hover, oklch(90% 0.06 270));
  }

  .option[aria-disabled="true"] {
    opacity: 0.4;
    cursor: not-allowed;
    pointer-events: none;
  }

  /* Option checkbox */
  .option-check {
    flex-shrink: 0;
    width: 16px;
    height: 16px;
    border: 1.5px solid var(--wa-checkbox-border, oklch(65% 0.03 260));
    border-radius: 4px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: transparent;
    transition: background 80ms ease, border-color 80ms ease, color 80ms ease;
  }

  .option[aria-selected="true"] .option-check {
    background: var(--wa-option-selected-bg, oklch(55% 0.22 270));
    border-color: var(--wa-option-selected-bg, oklch(55% 0.22 270));
    color: var(--wa-option-selected-color, #fff);
  }

  .option-text {
    flex: 1;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  /* ── Group labels ── */
  .group-label {
    padding: 7px 10px 2px;
    font-size: 0.72rem;
    font-weight: 700;
    letter-spacing: 0.07em;
    text-transform: uppercase;
    color: var(--wa-group-color, oklch(55% 0.22 270));
    cursor: default;
    user-select: none;
  }

  .group-label:not(:first-child) {
    margin-block-start: 0.25rem;
    padding-block-start: 0.625rem;
    border-block-start: 1px solid var(--wa-group-divider, oklch(90% 0.02 260));
  }

  /* ── No results ── */
  .no-results {
    padding: 12px 10px;
    text-align: center;
    color: var(--wa-placeholder-color, oklch(60% 0.02 260));
    font-size: 0.875rem;
    list-style: none;
  }

  /* ── Reduced motion ── */
  @media (prefers-reduced-motion: reduce) {
    .trigger, .chevron, .dropdown, .option, .option-check, .tag-remove, .select-all-indicator {
      transition: none;
    }
  }

  /* ── Forced colors (Windows High Contrast) ── */
  @media (forced-colors: active) {
    .trigger {
      border-color: ButtonText;
      background: ButtonFace;
      color: ButtonText;
      forced-color-adjust: none;
    }
    :host([open]) .trigger { outline: 2px solid Highlight; box-shadow: none; }
    .tag {
      border-color: ButtonText;
      background: ButtonFace;
      color: ButtonText;
      forced-color-adjust: none;
    }
    .dropdown {
      border-color: ButtonText;
      background: ButtonFace;
      forced-color-adjust: none;
    }
    .option-check,
    .select-all-indicator {
      forced-color-adjust: none;
      border-color: ButtonText;
      background: ButtonFace;
      color: ButtonFace;
    }
    .option[aria-selected="true"] .option-check,
    .select-all-btn[data-state="all"] .select-all-indicator,
    .select-all-btn[data-state="indeterminate"] .select-all-indicator {
      background: Highlight;
      border-color: Highlight;
      color: HighlightText;
    }
    .option.highlighted { outline: 1px solid Highlight; background: transparent; }
    .chevron { forced-color-adjust: auto; }
  }
</style>

<div class="trigger"
  part="trigger"
  role="presentation"
>
  <div class="selection" part="search">
    <input
      class="search-input"
      type="text"
      role="combobox"
      aria-expanded="false"
      aria-haspopup="listbox"
      aria-controls="${listboxId}"
      aria-autocomplete="list"
      aria-label="Select options"
      autocomplete="off"
      spellcheck="false"
    >
  </div>
  <div class="controls">
    <button class="cleaner" type="button" aria-label="Clear all selections" hidden>
      <svg width="10" height="10" viewBox="0 0 10 10" fill="none" stroke="currentColor" stroke-width="2.25" stroke-linecap="round" focusable="false" aria-hidden="true">
        <line x1="1" y1="1" x2="9" y2="9"/>
        <line x1="9" y1="1" x2="1" y2="9"/>
      </svg>
    </button>
    <span class="separator" aria-hidden="true"></span>
    <svg class="chevron" part="chevron" viewBox="0 0 12 12" aria-hidden="true" focusable="false">
      <path d="M2 4 L6 8 L10 4" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round"/>
    </svg>
  </div>
</div>

<div class="dropdown" part="dropdown">
  <div class="dropdown-header">
    <button class="select-all-btn" type="button" data-state="none">
      <span class="select-all-indicator" aria-hidden="true">
        <svg class="check-svg" width="11" height="11" viewBox="0 0 12 12" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
          <path d="M2 6L5 9L10 3"/>
        </svg>
        <svg class="dash-svg" width="10" height="3" viewBox="0 0 10 3" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round">
          <line x1="0" y1="1.5" x2="10" y2="1.5"/>
        </svg>
      </span>
      <span class="select-all-label">Select all</span>
    </button>
  </div>
  <ul
    class="options-list"
    role="listbox"
    id="${listboxId}"
    aria-multiselectable="true"
    tabindex="-1"
  ></ul>
</div>`;
  }
}

if (!customElements.get('wa-multi-select')) {
  customElements.define('wa-multi-select', WaMultiSelect);
}
